import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface ArticleCardProps {
  id: number;
  title: string;
  excerpt: string;
  thumbnail: string;
  author: string;
  date: string;
  tags: string[];
  views: number;
}

export default function ArticleCard({ id, title, excerpt, thumbnail, author, date, tags, views }: ArticleCardProps) {
  const { t } = useTranslation();

  const formatViews = (v: number) => {
    if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
    return v.toString();
  };

  return (
    <Link
      to={`/article/${id}`}
      className="group flex flex-col sm:flex-row gap-4 p-4 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300"
      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
    >
      <div className="relative w-full sm:w-48 flex-shrink-0 aspect-video sm:aspect-[4/3] rounded-lg overflow-hidden">
        <img
          src={thumbnail}
          alt={title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="flex flex-col justify-between flex-1 min-w-0">
        <div>
          <h3 className="font-heading text-sm md:text-base font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors leading-snug">
            {title}
          </h3>
          <p className="text-foreground-500 text-xs md:text-sm mt-1.5 line-clamp-2 leading-relaxed">
            {excerpt}
          </p>
        </div>
        <div className="flex items-center gap-4 mt-3 text-xs text-foreground-400 flex-wrap">
          <span className="flex items-center gap-1">
            <i className="ri-user-line"></i>
            {author}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-calendar-line"></i>
            {date}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-eye-line"></i>
            {formatViews(views)}{t('views')}
          </span>
          {tags.length > 0 && (
            <span className="flex items-center gap-1.5">
              {tags.slice(0, 2).map((tag) => (
                <span key={tag} className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-700 text-[10px] font-medium">
                  {tag}
                </span>
              ))}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}